import { DatePicker, DatePickerProps } from '@creditornot/cb-date-picker';
import { Calendar as CalendarIcon } from '@creditornot/cb-icons';
import { Input } from '@creditornot/cb-input';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import DatePickerPopover from 'common/components/DatePickerPopover';

const StyledPopover = styled(DatePickerPopover)`
  width: 100%;
`;

const StyledPickerWrapper = styled.div`
  padding: 4px 16px 20px;
`;

const getDefaultDateValue = (defaultValueOption: AdCampaignDateTimeDefaultValueOption, value?: DateTime) => {
  if (!value) {
    return defaultValueOption === 'today' ? new Date() : undefined;
  }
  return value.toJSDate();
};

export type AdCampaignDateTimeDefaultValueOption = 'empty' | 'today';

export type AdCampaignDatePickerProps = {
  defaultValueOption: AdCampaignDateTimeDefaultValueOption;
  disabled?: boolean;
  value?: DateTime;
  isInvalid?: boolean;
  placeholder?: string;
  disabledDays?: DatePickerProps['disabledDays'];
  onChange?(value?: DateTime): void;
};

export const AdCampaignDatePicker = ({
  disabled,
  defaultValueOption,
  value,
  isInvalid,
  placeholder,
  disabledDays,
  onChange,
}: AdCampaignDatePickerProps) => {
  const { locale } = useIntl();

  const [dateInputValue, setDateInputValue] = useState<Date | undefined>(
    getDefaultDateValue(defaultValueOption, value),
  );

  const formattedInputValue = useMemo(() => {
    if (!dateInputValue) {
      return '';
    }

    return DateTime.fromJSDate(dateInputValue).toLocaleString(DateTime.DATE_MED, { locale });
  }, [dateInputValue, locale]);

  const firstDayOfWeek = useMemo(() => {
    /**
     * FIXME: This one needs to be ignored for now (03/2023), as our current TS version
     * doesn't seem have types for Locale weekInfo.
     * It also has fallback to "1" as mozilla browsers don't support this API yet.
     * */
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const localizedFirstDay = new Intl.Locale(locale)?.weekInfo?.firstDay;
    if (localizedFirstDay >= 1 && localizedFirstDay <= 6) {
      return localizedFirstDay;
    }

    if (localizedFirstDay > 6) {
      return 0;
    }

    return 1;
  }, [locale]);

  return (
    <StyledPopover
      disabled={disabled}
      content={({ closePopover }) => (
        <StyledPickerWrapper>
          <DatePicker
            locale={locale}
            onChange={date => {
              if (date) {
                setDateInputValue(date);
                onChange?.(DateTime.fromJSDate(date));
              }
              closePopover();
            }}
            value={dateInputValue}
            firstDayOfWeek={firstDayOfWeek}
            disabledDays={disabledDays}
          />
        </StyledPickerWrapper>
      )}
      contentZIndex={100002}
    >
      {({ openPopover }) => (
        <Input
          disabled={disabled}
          icon={<CalendarIcon />}
          onClick={openPopover}
          readOnly
          value={formattedInputValue}
          invalid={isInvalid}
          placeholder={placeholder}
        />
      )}
    </StyledPopover>
  );
};
